import { useQuery } from '@tanstack/react-query';

import { useAccessToken } from '@hooks/useAccessToken';
import { fetchTeamFixtures } from '@api-service/team-fixtures';
import type {
  TeamFixturesParameters,
  TeamWithFixtures
} from '@api-service/team-fixtures';

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

export type UseTeamFixturesResult = {
  teams: TeamWithFixtures[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
};

// The full fixture ticker is a paying-member feature, so this hook is
// authenticated-only: the query is enabled only once an access token is
// present, and it is always forwarded. The public mini ticker uses
// useTeamFixturesPreview, which needs no token.
export const useTeamFixtures = (
  parameters: TeamFixturesParameters
): UseTeamFixturesResult => {
  const { accessToken, accessTokenIsFetching } = useAccessToken();

  const isRangeValid =
    parameters.gameweekStart >= 1 &&
    parameters.gameweekEnd >= parameters.gameweekStart;

  const isEnabled = isRangeValid && Boolean(accessToken);

  const {
    data,
    isLoading: queryIsLoading,
    isFetching,
    isError
  } = useQuery({
    queryKey: ['team-fixtures', parameters],
    queryFn: () =>
      fetchTeamFixtures({
        ...parameters,
        ...(accessToken ? { accessToken } : {})
      }),
    enabled: isEnabled,
    staleTime: FIVE_MINUTES_IN_MS,
    keepPreviousData: true,
    refetchOnWindowFocus: false
  });

  return {
    // A disabled query keeps its last cached data, so mask the teams when the
    // query is not enabled: without a valid token this authenticated-only hook
    // must not keep surfacing member fixture data (e.g. after token loss).
    teams: isEnabled ? data ?? [] : [],
    // Token resolution is part of loading: a member should see a loading state,
    // not an empty ticker, while the access token is still being fetched.
    isLoading:
      isRangeValid && (accessTokenIsFetching || (isEnabled && queryIsLoading)),
    isFetching,
    isError
  };
};
